import React, { useMemo } from 'react';
import ConstructSection from './ConstructSection';
import { Card, CardContent, CardHeader, CardTitle } from '../../../UI/card';

const PassagesDisplay = ({ contextData, passageData, userId }) => {
  const userData = useMemo(() => {    
    if (contextData && typeof contextData === 'object') {
      const { construct_label = [], behaviors = {} } = contextData;
      const constructs = {};
      const features = {};

      construct_label.forEach(item => {
        if (item.behavior_graph_key && typeof item.percentage !== 'undefined') {

          let constructKey = item.behavior_graph_key;

          constructs[constructKey] = item.percentage;

          const parts = constructKey.split('_');
          if (parts.length >= 2) {
            const gameType = parts[0]; // e.g., "Slide", "Stack", "Match"
            const constructType = parts.slice(1, -1).join('_'); // e.g., "Resilience", "Persistence"
            const category = parts[parts.length - 1]; // e.g., "Positive", "Negative", "Percentile"

            const featureKey = `${gameType}_${constructType}`;
            if (!features[featureKey]) {
              features[featureKey] = {};
            }

            const displayName = item.label || category.replace('Percentile', '');
            features[featureKey][displayName] = {
              human_readable_name: item.human_readable_name || displayName,
              percentage: item.percentage,
              raw_value: item.raw_value || item.percentage,
              original_variable_name: item.behavior_graph_key
            };
          }
        }
      });

      // Add behaviors data to features
      Object.entries(behaviors).forEach(([behaviorKey, behaviorList]) => {
        if (Array.isArray(behaviorList)) {
          behaviorList.forEach(behavior => {
            if (behavior && behavior.label && typeof behavior.percentage !== 'undefined') {
              let processedKey = behaviorKey;

              if (!behaviorKey.match(/^(Slide|Stack|Match)_/)) {
                if (behaviorKey.toLowerCase().includes('resilience')) {
                  processedKey = `Slide_${behaviorKey}`;
                } else if (behaviorKey.toLowerCase().includes('goalsetting')) {
                  processedKey = `Stack_${behaviorKey}`;
                } else if (behaviorKey.toLowerCase().includes('problemsolving') ||
                          behaviorKey.toLowerCase().includes('challengeseeking')) {
                  processedKey = `Match_${behaviorKey}`;
                } else if (behaviorKey.toLowerCase().includes('persistence')) {
                  processedKey = `Slide_${behaviorKey}`;
                }
              }

              const parts = processedKey.split('_');
              if (parts.length >= 2) {
                const gameType = parts[0];
                const constructType = parts.slice(1).join('_');
                const featureKey = `${gameType}_${constructType}`;

                if (!features[featureKey]) {
                  features[featureKey] = {};
                }

                features[featureKey][behavior.label] = {
                  human_readable_name: behavior.human_readable_name || behavior.label,
                  percentage: behavior.percentage,
                  raw_value: behavior.raw_value || behavior.percentage,
                  original_variable_name: behaviorKey
                };
              }
            }
          });
        }
      });

      return { constructs, features };
    }

    // Fallback to legacy passageData format if provided
    if (passageData && typeof passageData === 'object') {
      const userKey = Object.keys(passageData)[0];
      return userKey ? passageData[userKey] : null;
    }

    return null;
  }, [contextData, passageData]);

  const organizedFeatures = useMemo(() => {
    if (!userData?.features) return {};

    const organized = {};
    Object.entries(userData.features).forEach(([key, value]) => {
      const parts = key.split('_');
      const gameType = parts[0]; // Slide, Stack, Match
      const constructType = parts.slice(1).join('_'); // Resilience, Persistence, etc.

      if (!organized[constructType]) {
        organized[constructType] = {};
      }

      organized[constructType][gameType] = value;
    });

    return organized;
  }, [userData]);

  const constructPercentiles = useMemo(() => {
    if (!userData?.constructs) return {};
    return userData.constructs;
  }, [userData]);

  if (!userData || (!userData.constructs && !userData.features)) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="w-full max-w-md">
          <CardContent className="flex items-center justify-center p-8">
            <div className="text-center">
              <div className="text-gray-400 mb-2">
                <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <p className="text-gray-600">No passage data available</p>
              <p className="text-sm text-gray-500 mt-1">Data is being loaded or not yet available.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header Section */}
      <Card className="bg-gradient-to-r from-blue-500/80 to-purple-500/80 text-white border-0">
        <CardHeader>
          <CardTitle className="text-2xl font-semibold flex items-center">
            <svg className="w-8 h-8 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Performance Passages
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-blue-50">
            Detailed insights into your performance across different constructs and game types.
            Each section provides personalized feedback based on your gameplay behaviors and outcomes.
          </p>
        </CardContent>
      </Card>

      {/* Construct Sections */}
      <div className="space-y-6">
        {Object.entries(organizedFeatures).map(([constructType, gameData]) => (
          <ConstructSection
            key={constructType}
            constructType={constructType}
            gameData={gameData}
            constructPercentiles={constructPercentiles}
          />
        ))}
      </div>
    </div>
  );
};

export default PassagesDisplay;
